export type Hex = `#${string}`;
export type RGB = `rgb(${number}, ${number}, ${number})`;
export type HSL = `hsl(${number}, ${number}%, ${number}%)`;
export type Color = Hex | RGB | HSL;

export const isHex = (color: string): color is Hex => {
  // TODO: regex
  return color.startsWith('#');
};

export const isRGB = (color: string): color is RGB => {
  // TODO: regex
  return color.startsWith('rgb');
};

export const isHSL = (color: string): color is HSL => {
  // TODO: regex
  return color.startsWith('hsl');
};

export type RGBObj = {
  red: number;
  green: number;
  blue: number;
}
export type HSLObj = {
  h: number;
  s: number;
  l: number;
}

export type ColorScheme = 
  | "random"
  | "monochromatic" 
  | "analogous" 
  | "complementary" 
  | "split-complementary" 
  | "triadic" 
  | "tetradic" 
  | "square";

export const getRGB = (color: Color): RGBObj => {
  if (isRGB(color)) {
    const tupleStr = color.substring(4, color.length - 1);
    const vals = tupleStr.split(',');
    if (vals.length === 3) {
      return {
        red: Number(vals[0]),
        green: Number(vals[1]),
        blue: Number(vals[2]),
      };
    } else {
      return {
        red: 0,
        green: 0,
        blue: 0,
      };
    }
  } else if (isHex(color)){
    const tupleStr = color.substring(1);
    const r = tupleStr.substring(0, 2);
    const g = tupleStr.substring(2, 4);
    const b = tupleStr.substring(4, 6);
    if (r && g && b) {
      return {
          red: parseInt(r, 16),
          green: parseInt(g, 16),
          blue: parseInt(b, 16),
      };
    } else {
      return {
          red: 0,
          green: 0,
          blue: 0,
      };
    }
  } else {
    return {
      red: 0,
      green: 0,
      blue: 0,
    };
  }
};

export const parseHsl = (hsl: HSL): HSLObj => {
  const match = hsl.match(/hsl\((\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)%,\s*(\d+(?:\.\d+)?)%\)/);
  if (!match) throw new Error(`Invalid HSL format: ${hsl}`);
  
  return {
    h: parseFloat(match[1]),
    s: parseFloat(match[2]),
    l: parseFloat(match[3])
  };
};
  
export const createHsl = (h: number, s: number, l: number): HSL => {
  return `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`;
};

export const hexToHSL = (hex: Hex): HSL => {
  const tupleStr = hex.substring(1);
  const r = parseInt(tupleStr.substring(0, 2), 16);
  const g = parseInt(tupleStr.substring(2, 4), 16);
  const b = parseInt(tupleStr.substring(4, 6), 16);

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
          case r: h = (g - b) / d + (g < b ? 6 : 0); break;
          case g: h = (b - r) / d + 2; break;
          case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
  }

  return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
};

export const hslToHex = (hsl: HSL): Hex => {
  const { h, s, l } = parseHsl(hsl);
  const hNorm = h / 360;
  const sNorm = s / 100;
  const lNorm = l / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  let r, g, b;
  if (sNorm === 0) {
    r = g = b = lNorm;
  } else {
    const q = lNorm < 0.5 ? lNorm * (1 + sNorm) : lNorm + sNorm - lNorm * sNorm;
    const p = 2 * lNorm - q;
    r = hue2rgb(p, q, hNorm + 1/3);
    g = hue2rgb(p, q, hNorm);
    b = hue2rgb(p, q, hNorm - 1/3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};
